// pages/Admin.jsx
import React, { useState, useEffect, useCallback } from "react";
import { apiFetch } from "../api";
import "../styles/pages/_admin.css";
import { timeAgo, formatDate, formatDateTime, TimeAgo } from "../helpers/time";

// helpers
const ROLE_LABELS = { user: "User", moderator: "Mod", admin: "Admin" };
const ROLE_CYCLE  = ["user", "moderator", "admin"];

// Sub-components

function Toast({ message, type, onClose }) {
    useEffect(() => {
        const t = setTimeout(onClose, 3000);
        return () => clearTimeout(t);
    }, [onClose]);
    return (
        <div className={`adm-toast adm-toast--${type}`}>
            {type === "error" ? "✕" : "✓"} {message}
        </div>
    );
}

// Categories Tab

function CategoriesTab({ notify }) {
    const [categories, setCategories]   = useState([]);
    const [loading, setLoading]         = useState(true);
    const [editingId, setEditingId]     = useState(null);
    const [editForm, setEditForm]       = useState({ name: "", description: "" });
    const [newForm, setNewForm]         = useState({ name: "", description: "" });
    const [showNew, setShowNew]         = useState(false);
    const [saving, setSaving]           = useState(false);

    const load = useCallback(async () => {
        setLoading(true);
        try {
            const data = await apiFetch("/api/admin/categories");
            setCategories(data.categories);
        } catch (e) {
            notify(e.message, "error");
        } finally {
            setLoading(false);
        }
    }, [notify]);

    useEffect(() => { load(); }, [load]);

    // CREATE
    const handleCreate = async (e) => {
        e.preventDefault();
        if (!newForm.name.trim()) return;
        setSaving(true);
        try {
            const data = await apiFetch("/api/admin/categories", {
                method: "POST",
                body: JSON.stringify(newForm),
            });
            setCategories((prev) => [...prev, data.category]);
            setNewForm({ name: "", description: "" });
            setShowNew(false);
            notify("Category created", "success");
        } catch (e) {
            notify(e.message, "error");
        } finally {
            setSaving(false);
        }
    };

    // EDIT
    const startEdit = (cat) => {
        setEditingId(cat.id);
        setEditForm({ name: cat.name, description: cat.description });
    };
    const handleEdit = async (e) => {
        e.preventDefault();
        if (!editForm.name.trim()) return;
        setSaving(true);
        try {
            const data = await apiFetch(`/api/admin/categories/${editingId}`, {
                method: "PUT",
                body: JSON.stringify(editForm),
            });
            setCategories((prev) => prev.map((c) => c.id === editingId ? { ...c, ...data.category } : c));
            setEditingId(null);
            notify("Category updated", "success");
        } catch (e) {
            notify(e.message, "error");
        } finally {
            setSaving(false);
        }
    };

    // DELETE
    const handleDelete = async (cat) => {
        if (!window.confirm(`Delete "${cat.name}"? This will also delete all threads and messages inside it.`)) return;
        try {
            await apiFetch(`/api/admin/categories/${cat.id}`, { method: "DELETE" });
            setCategories((prev) => prev.filter((c) => c.id !== cat.id));
            notify("Category deleted", "success");
        } catch (e) {
            notify(e.message, "error");
        }
    };

    if (loading) return <div className="adm-loading">Loading categories…</div>;

    return (
        <div className="adm-categories">
            <div className="adm-section-header">
                <button className="adm-btn adm-btn--primary" onClick={() => setShowNew((v) => !v)}>
                    {showNew ? "Cancel" : "+ New Category"}
                </button>
            </div>

            {showNew && (
                <form className="adm-inline-form adm-inline-form--new" onSubmit={handleCreate}>
                    <input
                        className="adm-input"
                        placeholder="Category name"
                        value={newForm.name}
                        onChange={(e) => setNewForm((f) => ({ ...f, name: e.target.value }))}
                        required
                        autoFocus
                    />
                    <input
                        className="adm-input"
                        placeholder="Description (optional)"
                        value={newForm.description}
                        onChange={(e) => setNewForm((f) => ({ ...f, description: e.target.value }))}
                    />
                    <button className="adm-btn adm-btn--primary" type="submit" disabled={saving}>
                        {saving ? "Creating…" : "Create"}
                    </button>
                </form>
            )}

            {categories.length === 0 ? (
                <p className="adm-empty">No categories yet. Create one above.</p>
            ) : (
                <ul className="adm-cat-list">
                    {categories.map((cat) => (
                        <li key={cat.id} className="adm-cat-row">
                            {editingId === cat.id ? (
                                <form className="adm-inline-form" onSubmit={handleEdit}>
                                    <input
                                        className="adm-input"
                                        value={editForm.name}
                                        onChange={(e) => setEditForm((f) => ({ ...f, name: e.target.value }))}
                                        required
                                        autoFocus
                                    />
                                    <input
                                        className="adm-input"
                                        value={editForm.description}
                                        onChange={(e) => setEditForm((f) => ({ ...f, description: e.target.value }))}
                                        placeholder="Description"
                                    />
                                    <button className="adm-btn adm-btn--primary" type="submit" disabled={saving}>
                                        {saving ? "Saving…" : "Save"}
                                    </button>
                                    <button className="adm-btn" type="button" onClick={() => setEditingId(null)}>
                                        Cancel
                                    </button>
                                </form>
                            ) : (
                                <>
                                    <div className="adm-cat-info">
                                        <span className="adm-cat-name">{cat.name}</span>
                                        {cat.description && (
                                            <span className="adm-cat-desc">{cat.description}</span>
                                        )}
                                    </div>
                                    <span className="adm-cat-meta">{cat.thread_count} thread{cat.thread_count !== 1 ? "s" : ""}</span>
                                    <div className="adm-cat-actions">
                                        <button className="adm-btn adm-btn--sm" onClick={() => startEdit(cat)}>Edit</button>
                                        <button className="adm-btn adm-btn--sm adm-btn--danger" onClick={() => handleDelete(cat)}>Delete</button>
                                    </div>
                                </>
                            )}
                        </li>
                    ))}
                </ul>
            )}
        </div>
    );
}

// USERS TAB

function UsersTab({ notify }) {
    const [users, setUsers]     = useState([]);
    const [loading, setLoading] = useState(true);
    const [search, setSearch]   = useState("");
    const [query, setQuery]     = useState("");
    const [pagination, setPagination] = useState({ total: 0, page: 1, pages: 1 });
    const [updating, setUpdating] = useState(null);

    const load = useCallback(async (page = 1, q = query) => {
        setLoading(true);
        try {
            const params = new URLSearchParams({ page, limit: 50, search: q });
            const data = await apiFetch(`/api/admin/users?${params}`);
            setUsers(data.users);
            setPagination(data.pagination);
        } catch (e) {
            notify(e.message, "error");
        } finally {
            setLoading(false);
        }
    }, [query, notify]);

    useEffect(() => { load(1, ""); }, []);

    const handleSearch = (e) => {
        e.preventDefault();
        setQuery(search);
        load(1, search);
    };

    const updateUser = async (userId, patch) => {
        setUpdating(userId);
        try {
            const data = await apiFetch(`/api/admin/users/${userId}`, {
                method: "PATCH",
                body: JSON.stringify(patch),
            });
            setUsers((prev) => prev.map((u) => u.id === userId ? { ...u, ...data.user } : u));
            notify("User updated", "success");
        } catch (e) {
            notify(e.message, "error");
        } finally {
            setUpdating(null);
        }
    };

    const cycleRole = (user) => {
        const next = ROLE_CYCLE[(ROLE_CYCLE.indexOf(user.role) + 1) % ROLE_CYCLE.length];
        updateUser(user.id, { role: next });
    };

    const toggleBan = (user) => {
        updateUser(user.id, { banned: !user.banned });
    };

    return (
        <div className="adm-users">
            <form className="adm-search-row" onSubmit={handleSearch}>
                <input
                    className="adm-input adm-search-input"
                    placeholder="Search by username or email…"
                    value={search}
                    onChange={(e) => setSearch(e.target.value)}
                />
                <button className="adm-btn adm-btn--primary" type="submit">Search</button>
                {query && (
                    <button className="adm-btn" type="button" onClick={() => { setSearch(""); setQuery(""); load(1, ""); }}>
                        Clear
                    </button>
                )}
                <span className="adm-user-count">{pagination.total} user{pagination.total !== 1 ? "s" : ""}</span>
            </form>

            {loading ? (
                <div className="adm-loading">Loading users…</div>
            ) : users.length === 0 ? (
                <p className="adm-empty">No users found.</p>
            ) : (
                <div className="adm-table-wrap">
                    <table className="adm-table">
                        <thead>
                        <tr>
                            <th>User</th>
                            <th>Email</th>
                            <th>Posts</th>
                            <th>Joined</th>
                            <th>Role</th>
                            <th>Status</th>
                        </tr>
                        </thead>
                        <tbody>
                        {users.map((u) => (
                            <tr key={u.id} className={u.banned ? "adm-row--banned" : ""}>
                                <td>
                                    <div className="adm-user-cell">
                                        <span className="adm-user-id">#{u.id}</span>
                                        <span className="adm-username">{u.username}</span>
                                        {u.display_name && u.display_name !== u.username && (
                                            <span className="adm-displayname">{u.display_name}</span>
                                        )}
                                    </div>
                                </td>
                                <td className="adm-email">{u.email}</td>
                                <td className="adm-posts">{u.post_count}</td>
                                <td className="adm-date">{formatDate(u.created_at)}</td>
                                <td>
                                    <button
                                        className={`role role--${u.role}`}
                                        onClick={() => cycleRole(u)}
                                        disabled={updating === u.id}
                                        title="Click to cycle role"
                                    >
                                        {ROLE_LABELS[u.role] ?? u.role}
                                    </button>
                                </td>
                                <td>
                                    <button
                                        className={`adm-btn adm-btn--sm ${u.banned ? "adm-btn--unban" : "adm-btn--danger"}`}
                                        onClick={() => toggleBan(u)}
                                        disabled={updating === u.id}
                                    >
                                        {updating === u.id ? "…" : u.banned ? "Unban" : "Ban"}
                                    </button>
                                </td>
                            </tr>
                        ))}
                        </tbody>
                    </table>
                </div>
            )}

            {pagination.pages > 1 && (
                <div className="adm-pagination">
                    {Array.from({ length: pagination.pages }, (_, i) => i + 1).map((p) => (
                        <button
                            key={p}
                            className={`adm-page-btn${p === pagination.page ? " adm-page-btn--active" : ""}`}
                            onClick={() => load(p)}
                        >
                            {p}
                        </button>
                    ))}
                </div>
            )}
        </div>
    );
}

// MAIN EXPORT PAGE

const TABS = ["Categories", "Users"];

export default function Admin() {
    const [tab, setTab]       = useState("Categories");
    const [toast, setToast]   = useState(null);

    const notify = useCallback((message, type = "success") => {
        setToast({ message, type, key: Date.now() });
    }, []);

    return (
        <div className="adm-root">
            <header className="adm-header">
                <div className="adm-header-inner">
                    <h1 className="adm-title">
                        <span className="adm-title-accent">Admin</span> Panel
                    </h1>
                    <nav className="adm-tabs">
                        {TABS.map((t) => (
                            <button
                                key={t}
                                className={`adm-tab${tab === t ? " adm-tab--active" : ""}`}
                                onClick={() => setTab(t)}
                            >
                                {t}
                            </button>
                        ))}
                    </nav>
                </div>
            </header>

            <div className="adm-body">
                {tab === "Categories" && <CategoriesTab notify={notify} />}
                {tab === "Users"      && <UsersTab      notify={notify} />}
            </div>

            {toast && (
                <Toast
                    key={toast.key}
                    message={toast.message}
                    type={toast.type}
                    onClose={() => setToast(null)}
                />
            )}
        </div>
    );
}